import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { galleryData } from '../mock';

export const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'dairyFarm', label: 'Dairy Farm' },
    { id: 'horseStud', label: 'Horse Stud' },
    { id: 'facilities', label: 'Facilities' },
    { id: 'landscape', label: 'Landscape' }
  ];

  const getAllImages = () => {
    return [
      ...galleryData.dairyFarm.map(img => ({ url: img, category: 'dairyFarm' })),
      ...galleryData.horseStud.map(img => ({ url: img, category: 'horseStud' })),
      ...galleryData.facilities.map(img => ({ url: img, category: 'facilities' })),
      ...galleryData.landscape.map(img => ({ url: img, category: 'landscape' }))
    ];
  };

  const filteredImages = activeCategory === 'all'
    ? getAllImages()
    : getAllImages().filter(img => img.category === activeCategory);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${require("../images/coverpic.jpeg")})` }}
        />
        <div className="absolute inset-0 bg-farm-dark/70" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-6"
        >
          <h1 className="text-6xl md:text-7xl font-serif font-bold text-farm-cream mb-6">
            Our <span className="text-farm-gold">Gallery</span>
          </h1>
          <p className="text-xl text-farm-cream/80 max-w-2xl mx-auto">
            A glimpse into life at Sahiwal Farms
          </p>
        </motion.div>
      </section>

      {/* Gallery Section */}
      <section ref={containerRef} className="py-32 bg-farm-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap justify-center gap-4 mb-16"
          >
            {categories.map((category) => (
              <motion.button
                key={category.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${activeCategory === category.id
                  ? 'bg-farm-gold text-farm-dark shadow-lg shadow-farm-gold/50'
                  : 'bg-farm-dark/50 border border-farm-green/20 text-farm-cream hover:border-farm-gold/50'
                  }`}
              >
                {category.label}
              </motion.button>
            ))}
          </motion.div>

          {/* Masonry Gallery */}
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredImages.map((image, index) => (
              <motion.div
                key={`${image.category}-${index}`}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                whileHover={{ y: -10 }}
                className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-farm-gold/20 transition-all duration-500"
              >
                <div className="relative h-80 overflow-hidden">
                  <motion.img
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                    src={image.url}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-farm-dark/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};